/**
 * __DAY_GRAPH__ (2026-10-20) View -> Day graph. Henry: "a graph with a gauge needle that sweeps along the screen from
 * left to right and aligns itself with real time which will be displayed as columns ... driver names as rows and their
 * assigned jobs in the cells."
 * The hub places every job (services/dayGraphService.js; the Dispatch app's Board tab draws the same answer):
 * a job sits at its actual start once started, else its planned start; its length is the PROJECTED duration (route
 * drive + time at each stop by cargo size / weight), growing to the needle when it runs over; complete = actual start ->
 * finish. A faint outline shows the planned place when the actual one differs. Click a job: the job's details.
 * Columns and hidden empty time: Settings -> Day graph.
 * __DAY_GRAPH_ZOOM__ − / + / Fit day (anchored on the needle), Ctrl + wheel or a trackpad pinch (anchored on the pointer);
 * remembered for each login. The hour labels thin out / fill in with the zoom; narrow bars drop their text.
 * __TRUE_DURATION__ (2026-10-28) bars from the job's first stop, as long as its own estimate (a faded right end while the
 * end is an estimate); a route / combo is ONE block with a part per job and a divider line between them, each part
 * with its own colour, text and hover text.
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import LoadDetailsModal from './LoadDetailsModal';
import { fetchDayGraph } from '../services/dayGraphService';
import { layout, torontoDay, shiftDay, clock, needleMin, barText, segmentText, statusColor, clampZoom, fitZoom, zoomStep, minuteAt, anchorScroll, readZoom, ZOOM_MAX, DONE_COLOR, LATE_COLOR } from '../utils/dayGraph';

const NAME_W = 150;
const HEAD_H = 30;
const LOAD_SELECT = `
  *,
  customer:customers(company_name, phone),
  driver:drivers(user_id(name), phone),
  truck:trucks(truck_number, license_plate),
  load_stops(id, stop_type, stop_sequence, address, place_name, time_window_start)
`;
const LEGEND = [['assigned', 'Assigned'], ['en_route', 'En route'], ['at_pickup', 'At pickup / loading'], ['in_transit', 'In transit'],
  ['at_delivery', 'At delivery / unloading'], ['pending_completion', 'Dropped off · confirm']];   // __STATUS_TRUTH__ every started status has a key

function DayGraphView({ userId }) {
  const allKey = 'hb_daygraph_all_' + (userId || '');
  const [day, setDay] = useState(torontoDay());
  const [showAll, setShowAll] = useState(() => { try { return localStorage.getItem(allKey) === '1'; } catch (e) { return false; } });
  const [graph, setGraph] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(Date.now());
  const [selected, setSelected] = useState(null);
  const [combo, setCombo] = useState(null);
  const scroller = useRef(null);
  const scrolledFor = useRef('');
  // __DAY_GRAPH_ZOOM__ 'fit' (the day's columns fill the width) or px a minute
  const zoomKey = 'hb_daygraph_zoom_' + (userId || '');
  const [zoom, setZoom] = useState(() => { try { return readZoom(localStorage.getItem(zoomKey)); } catch (e) { return 'fit'; } });
  const [viewW, setViewW] = useState(900);
  const pending = useRef(null);      // { m, sx }: keep minute m at sx px after the zoom
  const live = useRef({});           // the current ppm / window / needle for the wheel handler

  const load = useCallback(async () => {
    const r = await fetchDayGraph(day, showAll);
    if (r.ok) { setGraph(r.graph); setFetchedAt(Date.now()); setTick(Date.now()); setErr(''); } else setErr(r.message);
    setLoading(false);
  }, [day, showAll]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 30000);   // the needle moves on between fetches
    return () => clearInterval(t);
  }, []);
  useEffect(() => { try { localStorage.setItem(allKey, showAll ? '1' : '0'); } catch (e) { /* a convenience */ } }, [allKey, showAll]);

  useEffect(() => { try { localStorage.setItem(zoomKey, String(zoom)); } catch (e) { /* a convenience */ } }, [zoomKey, zoom]);
  useEffect(() => {
    const measure = () => { if (scroller.current) setViewW(Math.max(200, scroller.current.clientWidth - NAME_W)); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [graph]);

  const needle = graph ? needleMin(graph, fetchedAt, tick) : null;
  const fitP = graph ? fitZoom(viewW, graph.window) : 1;          // zoomed all the way out = the whole day on one screen
  const ppm = graph ? (zoom === 'fit' ? fitP : Math.max(fitP, clampZoom(zoom))) : 1;
  const L = graph ? layout(graph, ppm, needle) : null;
  live.current = { ppm, fitP, win: L ? L.window : null, needleX: L ? L.needleX : null };

  // keep the anchor minute where it was on screen after a zoom
  useLayoutEffect(() => {
    if (!pending.current || !scroller.current || !L) return;
    scroller.current.scrollLeft = anchorScroll(L.window, pending.current.m, pending.current.sx, ppm);
    pending.current = null;
  });
  const zoomTo = useCallback((next, sx) => {
    const { ppm: p, win, fitP: f } = live.current;
    if (!win || !scroller.current) return;
    pending.current = { m: minuteAt(win, scroller.current.scrollLeft, sx, p), sx };
    const z = Math.min(ZOOM_MAX, Math.max(f, clampZoom(next)));
    setZoom(z <= f + 1e-6 ? 'fit' : z);
  }, []);
  // buttons: anchored on the needle when it is on screen, else the middle
  const zoomButton = (dir) => {
    const { ppm: p, needleX } = live.current;
    const sl = scroller.current ? scroller.current.scrollLeft : 0;
    const onScreen = needleX != null && needleX - sl >= 0 && needleX - sl <= viewW;
    zoomTo(zoomStep(p, dir), onScreen ? needleX - sl : viewW / 2);
  };
  const fitDay = () => { pending.current = { m: L ? L.window.start : 0, sx: 0 }; setZoom('fit'); };
  // Ctrl + wheel / a trackpad pinch (the browser sends it as Ctrl + wheel): zoom on the pointer, not the page
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const sx = Math.max(0, e.clientX - r.left - NAME_W);
      zoomTo(live.current.ppm * Math.exp(-e.deltaY * 0.002), sx);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [graph, zoomTo]);

  // bring the needle into view once per day shown
  useEffect(() => {
    if (!L || !scroller.current || scrolledFor.current === (graph && graph.date)) return;
    scrolledFor.current = graph.date;
    const target = L.needleX != null ? L.needleX : ((L.rows.flatMap((r) => r.bars)[0] || {}).x || 0);
    scroller.current.scrollLeft = Math.max(0, target - (scroller.current.clientWidth - NAME_W) / 3);
  });

  const openJob = async (id) => {
    if (!id) return;
    const { data, error } = await supabase.from('loads').select(LOAD_SELECT).eq('id', id).single();
    if (!error && data) { setCombo(null); setSelected(data); } else setErr('That job could not be opened.');
  };
  // __TRELLO_CARDS_ON_GRAPH__ a Trello-only card has no hauling job: its details, read-only
  const onBar = (b) => { if (b.members) setCombo(b); else if (b.trello_only) setCombo({ ...b, members: null, trelloOnly: true }); else openJob(b.id); };

  const btn = { padding: '0.35rem 0.7rem', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-2)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.85rem' };
  const today = torontoDay();

  return (
    <div data-day-graph>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
        <button style={btn} onClick={() => setDay(shiftDay(day, -1))} aria-label="Previous day">‹</button>
        <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} style={{ ...btn, padding: '0.3rem 0.5rem' }} aria-label="Day" />
        <button style={btn} onClick={() => setDay(shiftDay(day, 1))} aria-label="Next day">›</button>
        {day !== today && <button style={btn} onClick={() => setDay(today)}>Today</button>}
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: '0.5rem' }} data-zoom>
          <button style={btn} onClick={() => zoomButton(-1)} disabled={ppm <= fitP + 1e-6} aria-label="Zoom out" title="Zoom out (or Ctrl + wheel)">−</button>
          <button style={btn} onClick={() => zoomButton(1)} disabled={ppm >= ZOOM_MAX - 1e-6} aria-label="Zoom in" title="Zoom in (or Ctrl + wheel)">+</button>
          <button style={{ ...btn, fontWeight: zoom === 'fit' ? 700 : 400 }} onClick={fitDay} title="The whole day on one screen">Fit day</button>
        </span>
        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show all drivers
        </label>
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {loading ? 'Loading…' : graph ? (graph.today ? 'Live — now ' + clock(needle) : 'Another day — no needle') : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
        {LEGEND.map(([k, t]) => (
          <span key={k}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: statusColor(k), marginRight: 4, verticalAlign: 'middle' }} />{t}</span>
        ))}
        {/* __DONE_TODAY__ */}
        <span><span style={{ display: 'inline-block', width: 14, height: 10, borderRadius: 2, background: DONE_COLOR, color: '#fff', fontSize: 8, lineHeight: '10px', textAlign: 'center', marginRight: 4, verticalAlign: 'middle' }}>✓</span>Done today</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 10, border: '1px solid var(--text-muted)', borderLeft: '4px solid ' + LATE_COLOR, marginRight: 4, verticalAlign: 'middle', boxSizing: 'border-box' }} />Late (not started)</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 10, border: '1px dashed var(--text-muted)', marginRight: 4, verticalAlign: 'middle' }} />Planned place</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 10, marginRight: 4, verticalAlign: 'middle', background: 'repeating-linear-gradient(45deg, rgba(248,81,73,.55) 0 4px, transparent 4px 8px)' }} />Over its projected time</span>
        {/* __TRUE_DURATION__ */}
        <span><span style={{ display: 'inline-block', width: 14, height: 10, marginRight: 4, verticalAlign: 'middle', background: 'linear-gradient(to right, ' + statusColor('assigned') + ', transparent)' }} />Estimated end</span>
        <span><span style={{ display: 'inline-block', width: 22, height: 10, marginRight: 4, verticalAlign: 'middle', background: statusColor('assigned') + '55', borderLeft: '4px solid ' + statusColor('assigned'), boxShadow: 'inset -9px 0 0 -6px var(--surface-1, #0d1117)' }} />Route: one block, a part per job</span>
      </div>
      {!!err && <div style={{ color: 'var(--color-danger, #f85149)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{err}</div>}
      {graph && graph.no_time && graph.no_time.length > 0 && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
          No time set ({graph.no_time.length}):{' '}
          {graph.no_time.map((j) => (
            <button key={j.id} style={{ ...btn, padding: '0.1rem 0.5rem', marginRight: 4, fontSize: '0.75rem' }} onClick={() => openJob(j.id)} title={(j.from || '') + (j.to ? ' → ' + j.to : '')}>{j.customer}</button>
          ))}
        </div>
      )}
      {L && (
        <div ref={scroller} style={{ overflow: 'auto', maxHeight: 'calc(100vh - 380px)', minHeight: 320, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--surface-1, var(--bg-primary))', position: 'relative' }}>
          <div style={{ position: 'relative', width: NAME_W + L.width, minWidth: '100%' }}>
            {/* the hour columns */}
            <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 4, background: 'var(--surface-2)', borderBottom: '1px solid var(--border-default)', height: HEAD_H }}>
              <div style={{ position: 'sticky', left: 0, zIndex: 5, width: NAME_W, boxSizing: 'border-box', flex: '0 0 auto', background: 'var(--surface-2)', fontSize: '0.75rem', color: 'var(--text-muted)', padding: '7px 8px', borderRight: '1px solid var(--border-default)' }}>Driver</div>
              <div style={{ position: 'relative', width: L.width, flex: '0 0 auto' }}>
                {L.labels.map((h) => (
                  <div key={h.min} style={{ position: 'absolute', left: h.x, top: 0, height: HEAD_H, borderLeft: '1px solid var(--border-default)', paddingLeft: 4, fontSize: '0.72rem', color: h.min % 60 ? 'var(--text-muted)' : 'var(--text-secondary)', lineHeight: HEAD_H + 'px', whiteSpace: 'nowrap' }}>{h.label}</div>
                ))}
                {L.ticks.map((t) => <div key={t.min} style={{ position: 'absolute', left: t.x, bottom: 0, height: 6, borderLeft: '1px solid var(--border-default)' }} />)}
                {L.needleX != null && (
                  <div style={{ position: 'absolute', left: L.needleX - 28, top: 4, width: 56, textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: '#fff', background: '#f85149', borderRadius: 4, transition: 'left 30s linear' }}>{clock(needle)}</div>
                )}
              </div>
            </div>
            {/* the rows */}
            <div style={{ position: 'relative' }}>
              <div aria-hidden style={{ position: 'absolute', left: NAME_W, top: 0, bottom: 0, width: L.width, pointerEvents: 'none' }}>
                {L.hours.map((h) => <div key={h.min} style={{ position: 'absolute', left: h.x, top: 0, bottom: 0, borderLeft: '1px solid var(--border-default)', opacity: 0.6 }} />)}
              </div>
              {L.rows.map((r) => (
                <div key={r.driver_id || 'unassigned'} style={{ display: 'flex', borderBottom: '1px solid var(--border-default)' }}>
                  <div style={{ position: 'sticky', left: 0, zIndex: 3, width: NAME_W, boxSizing: 'border-box', flex: '0 0 auto', background: 'var(--surface-2)', padding: '8px', fontSize: '0.85rem', fontWeight: 600, color: r.driver_id ? 'var(--text-primary)' : 'var(--text-muted)', borderRight: '1px solid var(--border-default)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name}>
                    {r.name}
                  </div>
                  <div style={{ position: 'relative', width: L.width, height: r.height, flex: '0 0 auto' }}>
                    {r.bars.filter((b) => b.ghostBox).map((b) => (
                      <div key={'g' + b.id} aria-hidden style={{ position: 'absolute', left: b.ghostBox.x, top: b.ghostBox.top, width: b.ghostBox.w, height: b.h, border: '1px dashed ' + b.color, borderRadius: 6, opacity: 0.45, pointerEvents: 'none' }} />
                    ))}
                    {r.bars.map((b) => (
                      <div key={b.id} data-bar={b.id} onClick={() => onBar(b)} title={barText(b)}
                        style={{ position: 'absolute', left: b.x, top: b.top, width: b.w, height: b.h, background: b.segs ? 'transparent' : b.done ? b.color + '66' : b.trello_only ? 'transparent' : b.color + '33', border: '1px ' + (b.trello_only ? 'dashed ' : 'solid ') + b.color, borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: b.edge || b.color, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', boxSizing: 'border-box', padding: b.text === 'none' ? 0 : '2px 6px', fontSize: '0.72rem', lineHeight: '15px', color: 'var(--text-primary)' }}>
                        {b.segs && b.segs.map((sg, i) => (
                          <div key={sg.id} data-seg={sg.id} title={segmentText(sg, i)}
                            style={{ position: 'absolute', left: sg.x - 4, top: 0, bottom: 0, width: sg.w, boxSizing: 'border-box', background: sg.color + (sg.done ? '66' : '33'),
                              borderLeft: sg.divider ? '3px solid var(--surface-1, #0d1117)' : 'none', padding: sg.text === 'none' ? 0 : '2px 6px 2px ' + (i === 0 ? 6 : 5) + 'px', overflow: 'hidden' }}>
                            {sg.divider && <div aria-hidden data-divider style={{ position: 'absolute', left: -1, top: 3, bottom: 3, borderLeft: '1px solid ' + b.color }} />}
                            {sg.text !== 'none' && <div style={{ position: 'relative', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(sg.done ? '✓ ' : '') + (sg.customer || '')}</div>}
                            {sg.text === 'full' && <div style={{ position: 'relative', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>{(sg.from || '') + (sg.to ? ' → ' + sg.to : '')}</div>}
                          </div>
                        ))}
                        {b.overrun && b.overX != null && (
                          <div aria-hidden style={{ position: 'absolute', left: b.overX, top: 0, bottom: 0, right: 0, background: 'repeating-linear-gradient(45deg, rgba(248,81,73,.45) 0 6px, transparent 6px 12px)' }} />
                        )}
                        {b.estEnd && b.w >= 30 && (
                          <div aria-hidden data-est-end style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: Math.min(14, b.w / 4), background: 'linear-gradient(to right, transparent, var(--surface-1, #0d1117))', opacity: 0.75, pointerEvents: 'none' }} />
                        )}
                        {!b.segs && b.text !== 'none' && <div style={{ position: 'relative', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(b.done ? '✓ ' : '') + b.customer}</div>}
                        {!b.segs && b.text === 'full' && <div style={{ position: 'relative', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>{(b.from || '') + (b.to ? ' → ' + b.to : '')}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {L.needleX != null && (
                <div data-needle aria-label={'Now ' + clock(needle)} style={{ position: 'absolute', left: NAME_W + L.needleX - 1, top: 0, bottom: 0, width: 2, background: '#f85149', zIndex: 2, transition: 'left 30s linear', pointerEvents: 'none', boxShadow: '0 0 4px rgba(248,81,73,.7)' }} />
              )}
            </div>
          </div>
        </div>
      )}
      {combo && (
        <div onClick={() => setCombo(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '1rem', minWidth: 300, maxWidth: 480, border: '1px solid var(--border-default)' }}>
            <h3 style={{ marginTop: 0 }}>{combo.customer}</h3>
            {combo.trelloOnly ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginBottom: '0.75rem' }}>
                {barText(combo)}{'\n\nTrello only: this card has no hauling job yet, so it is shown read-only, placed by the list\'s plan.'}
              </div>
            ) : (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>{clock(combo.start)} – {clock(combo.end)} · one route block{combo.end_kind === 'estimated' ? ' (est. end)' : ''}</div>
            )}
            {(combo.members || []).map((m) => (
              <button key={m.id} onClick={() => openJob(m.id)} style={{ ...btn, display: 'block', width: '100%', textAlign: 'left', marginBottom: 6 }}>
                <b>{m.customer}</b> · {m.status_label}{m.truck ? ' · ' + m.truck : ''}<br /><span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{(m.from || '') + (m.to ? ' → ' + m.to : '')}</span>
                {m.seg_start != null && <><br /><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{clock(m.seg_start)} – {clock(m.seg_end)}{m.drive_min != null ? ' · ' + m.drive_min + ' min drive + ' + m.dwell_min + ' min at stops' : ''}{m.drive_in ? ' · ' + m.drive_in + ' min drive from the job before' : ''}</span></>}
              </button>
            ))}
            <button style={btn} onClick={() => setCombo(null)}>Close</button>
          </div>
        </div>
      )}
      {selected && (
        <LoadDetailsModal load={selected} onClose={() => setSelected(null)} onUpdate={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

export default DayGraphView;
